import React, { useState } from 'react';

import { useConfirmPasswordReset } from '@vlabs/api/artemy/client';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ChangeUserPasswordForm, defaultValues } from './ChangeUserPasswordForm';
import st from './ChangeUserPasswordPage.module.sass';
import { useChangeUserPasswordPageTranslation } from './i18n';
import type { ApiError, ChangeUserPasswordFormValues } from './types';

const getTokenFromSearch = (search: string): string | undefined => {
  const params = new URLSearchParams(search);
  // токен приходит из ссылки сброса пароля: /auth/password-reset?token=...
  return params.get('token') ?? undefined;
};

const getErrorText = (error: unknown, t: (key: string) => string): string => {
  const apiError = error as ApiError;
  const status = apiError.status ?? apiError.response?.status;

  // для истекшего токена апи отвечает 422
  if (status === 422) return t('error.reset_expired');

  return t('error.password_not_changed');
};

function ChangeUserPasswordPage(): React.JSX.Element {
  const { t } = useChangeUserPasswordPageTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const token = getTokenFromSearch(search);
  const [formError, setFormError] = useState<string>();

  const confirmPasswordReset = useConfirmPasswordReset({
    request: {
      headers: {
        Authorization: token,
      },
    },
  });

  const onSubmit = async ({ password }: ChangeUserPasswordFormValues): Promise<void> => {
    setFormError(undefined);

    try {
      await confirmPasswordReset.mutateAsync({ data: { password } });
      toast.success(t('success'));
      history.push('/');
    } catch (error) {
      setFormError(getErrorText(error, t));
    }
  };

  return (
    <Page>
      <PageContent className={st.Root}>
        <div className={st.Card}>
          <ChangeUserPasswordForm
            defaultValues={defaultValues}
            formError={formError}
            isLoading={confirmPasswordReset.isPending}
            isSubmitDisabled={confirmPasswordReset.isPending || !token}
            onChange={() => setFormError(undefined)}
            onSubmit={onSubmit}
          />
        </div>
      </PageContent>
    </Page>
  );
}

export { ChangeUserPasswordPage };
